import * as React from 'react'

import NextLink from 'next/link'
import styled, { css } from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'

import * as api from '@owl-nest/api-client/next'
import * as hooks from '@owl-nest/hooks'
import { t, tc } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as duvet from '@ulule/duvet'

import { useDisplayMode } from '../hooks/useDisplayMode'
import { getCurrentLocale } from '../router'

type PostListProps = {
  project: models.project.ProjectForOwner
  projectNewsResponse: hooks.Response<api.NewsListPage>
}

export function PostList({ projectNewsResponse, project }: PostListProps): React.ReactElement {
  const router = hooks.useNextRouter()
  const currentLocale = getCurrentLocale(router)
  const displayMode = useDisplayMode(project)
  const isEditMode = displayMode === 'edit'
  const isMember = models.project.isUserMember(project)

  return (
    <PostListLayout
      className="membership-section"
      id="posts"
      isMember={isMember}
      isThereAnyPost={Boolean(projectNewsResponse.data?.meta.total_count)}
      isEditMode={isEditMode}
    >
      <PostListHeader>
        <plume.styles.heading.XS asDesktop="L">
          {tc('[highlight: Posts]', {
            highlight: <plume.HighlightedTitle />,
          })}
        </plume.styles.heading.XS>
        {isEditMode && (
          <a href={`/projects/${project.id}/backoffice/news`}>
            <plume.EditButton glyph={<plume.glyphs.stroke.Edit size={18} />}>{t('Edit')}</plume.EditButton>
          </a>
        )}
      </PostListHeader>

      {isEditMode && (
        <plume.Placeholder clickTarget="add-post">
          <plume.Link href={`/projects/${project.id}/backoffice/news/create`} id="add-post">
            <plume.EditButton glyph={<plume.glyphs.stroke.Plus size={18} />} onlyGlyph />
            {t('Add a post')}
          </plume.Link>
        </plume.Placeholder>
      )}
      {projectNewsResponse.status === hooks.QueryStatus.SUCCESS && (
        <>
          {projectNewsResponse.data.news?.map((newsPiece) => {
            const userHasReadAccess = Boolean(newsPiece.permissions.self?.includes('read'))

            return (
              <duvet.NewsCard
                href={`/${project.slug}/news/${newsPiece.slug}-${newsPiece.id}/`}
                isEditMode={isEditMode}
                key={newsPiece.id}
                locale={currentLocale}
                news={newsPiece}
                project={project}
              />
            )
          })}
          {projectNewsResponse.data.meta.total_count > 3 && (
            <NextLink href={`/${project.slug}/news`}>
              <plume.Button kind="primary" size="small">
                {t('See more posts')}
              </plume.Button>
            </NextLink>
          )}
        </>
      )}
    </PostListLayout>
  )
}

const PostListLayout = styled.div<{ isMember?: boolean; isThereAnyPost?: boolean; isEditMode?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-inline: 16px;
  gap: 24px;
  position: relative;
  margin-bottom: 64px;
  margin-top: 40px;

  ${({ isEditMode, theme }) => {
    if (isEditMode) {
      return css`
        &::before {
          background-color: ${theme.colors.background};
          bottom: -25px;
          content: '';
          display: block;
          height: auto;
          left: 0;
          position: absolute;
          top: 286px;
          width: 100%;
          z-index: -1;

          @media screen and ${plume.BREAKPOINTS.TABLET} {
            margin-top: 80px;
            top: 230px;
            bottom: -56px;
          }
        }
      `
    } else {
      return css`
        padding-top: 40px;

        &::before {
          background-color: ${theme.colors.background};
          bottom: -25px;
          content: '';
          display: block;
          height: auto;
          left: 0;
          position: absolute;
          top: 185px;
          width: 100%;
          z-index: -1;

          @media screen and ${plume.BREAKPOINTS.TABLET} {
            margin-top: 0px;
            top: 180px;
            bottom: -56px;
          }
        }
      `
    }
  }}

  ${plume.Placeholder} {
    display: flex;
    justify-content: center;
    max-width: 516px;

    ${plume.EditButton} {
      margin-right: 16px;
      vertical-align: middle;
    }
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-bottom: ${(props) => (!props.isThereAnyPost ? '80px' : '136px')}; // (80px + 56px from the ::before element)
    margin-top: ${(props) => (props.isMember ? '80px' : '75px')};
    padding-top: 0px;
  }
`

const PostListHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-bottom: 6px;
  position: relative;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-bottom: 20px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    margin-bottom: 16px;
  }

  a {
    position: relative;
    top: 16px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    a {
      position: absolute;
      right: -140px;
      top: 0;
    }
  }
`
